"use client";

import Icon from "@/components/ui/AppIcon";

export type FilterState = {
  category: string;
  ageRange: string;
  priceRange: string;
  sortBy: string;
};

interface FilterSidebarProps {
  filters: FilterState;
  onFilterChange: (key: keyof FilterState, value: string) => void;
  onReset: () => void;
  productCount: number;
}

const categories = [
  { value: "all", label: "All Products", emoji: "📚" },
  { value: "alphabet", label: "Alphabet Books", emoji: "🔤" },
  { value: "numbers", label: "Numbers Books", emoji: "🔢" },
  { value: "coloring", label: "Coloring Books", emoji: "🎨" },
  { value: "tracing", label: "Tracing & Writing", emoji: "✏️" },
  { value: "fantasy", label: "Fantasy & Magic", emoji: "🦄" },
  { value: "accessories", label: "Accessories", emoji: "🖍️" },
];

const ageRanges = [
  { value: "all", label: "All Ages" },
  { value: "2-4", label: "Ages 2–4" },
  { value: "3-6", label: "Ages 3–6" },
  { value: "4-8", label: "Ages 4–8" },
  { value: "6-10", label: "Ages 6–10" },
];

const priceRanges = [
  { value: "all", label: "Any Price" },
  { value: "0-10", label: "Under $10" },
  { value: "10-15", label: "$10 – $15" },
  { value: "15-25", label: "$15 – $25" },
  { value: "25+", label: "$25+" },
];

const sortOptions = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Newest First" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
  { value: "rating", label: "Highest Rated" },
  { value: "bestselling", label: "Bestselling" },
];

export default function FilterSidebar({
  filters,
  onFilterChange,
  onReset,
  productCount,
}: FilterSidebarProps) {
  const hasActiveFilters =
    filters.category !== "all" ||
    filters.ageRange !== "all" ||
    filters.priceRange !== "all" ||
    filters.sortBy !== "featured";

  return (
    <aside className="w-full lg:w-64 shrink-0">
      <div className="bg-white rounded-3xl shadow-dreamy p-5 sm:p-6 sticky top-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-fraunces font-bold text-plum text-lg flex items-center gap-2">
            <Icon name="AdjustmentsHorizontalIcon" size={20} />
            Filters
          </h2>
          {hasActiveFilters && (
            <button
              onClick={onReset}
              className="text-xs font-bold text-plum-light hover:text-plum flex items-center gap-1 transition-colors min-h-[44px] px-2"
            >
              <Icon name="XMarkIcon" size={16} />
              Reset
            </button>
          )}
        </div>

        <div className="text-xs text-plum/50 font-medium mb-5 bg-plum-50 rounded-xl px-3 py-3">
          Showing <span className="font-black text-plum">{productCount}</span> products
        </div>

        {/* Sort */}
        <div className="mb-6">
          <h3 className="text-xs font-black text-plum uppercase tracking-wider mb-3">Sort By</h3>
          <select
            value={filters.sortBy}
            onChange={(e) => onFilterChange("sortBy", e.target.value)}
            className="w-full text-sm text-plum font-medium bg-plum-50 border border-lavender/40 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-lavender min-h-[48px]"
            aria-label="Sort products"
          >
            {sortOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Category */}
        <div className="mb-6">
          <h3 className="text-xs font-black text-plum uppercase tracking-wider mb-3">Category</h3>
          <div className="space-y-2">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => onFilterChange("category", cat.value)}
                className={`w-full flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 active:scale-95 min-h-[48px] ${
                  filters.category === cat.value
                    ? "bg-plum text-white shadow-dreamy"
                    : "text-plum/70 hover:bg-plum-50 hover:text-plum"
                }`}
              >
                <span className="text-base" aria-hidden="true">{cat.emoji}</span>
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Age range */}
        <div className="mb-6">
          <h3 className="text-xs font-black text-plum uppercase tracking-wider mb-3">Age Range</h3>
          <div className="flex flex-wrap gap-2">
            {ageRanges.map((age) => (
              <button
                key={age.value}
                onClick={() => onFilterChange("ageRange", age.value)}
                className={`filter-chip transition-all duration-200 active:scale-95 min-h-[44px] px-4 py-2 ${filters.ageRange === age.value ? "active" : ""}`}
              >
                {age.label}
              </button>
            ))}
          </div>
        </div>

        {/* Price range */}
        <div className="mb-6">
          <h3 className="text-xs font-black text-plum uppercase tracking-wider mb-3">Price Range</h3>
          <div className="flex flex-wrap gap-2">
            {priceRanges.map((pr) => (
              <button
                key={pr.value}
                onClick={() => onFilterChange("priceRange", pr.value)}
                className={`filter-chip transition-all duration-200 active:scale-95 min-h-[44px] px-4 py-2 ${filters.priceRange === pr.value ? "active" : ""}`}
              >
                {pr.label}
              </button>
            ))}
          </div>
        </div>

        {/* Contact CTA */}
        <div className="bg-plum-100 rounded-2xl p-4 text-center">
          <div className="text-2xl mb-2">💬</div>
          <p className="text-xs font-bold text-plum mb-3">Need help choosing?</p>
          <a
            href="https://wa.me/256764590167"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-sm font-bold text-white bg-green-500 hover:bg-green-600 rounded-xl py-3 transition-all duration-200 active:scale-95 min-h-[48px] flex items-center justify-center"
          >
            Chat on WhatsApp
          </a>
        </div>
      </div>
    </aside>
  );
}